import React, { useCallback } from 'react';
import { Loading } from 'carbon-components-react';
import { ReportTableProps } from '../../types';

/**
 * ReportTable component that displays server-rendered HTML report in an iframe.
 *
 * WHY: Using iframe with srcdoc allows us to safely render server-generated HTML
 * while keeping it isolated from the main application DOM.
 */
export const ReportTable: React.FC<ReportTableProps> = ({ html, loading }) => {
  /**
   * Resize iframe to match content height.
   *
   * WHY: Iframes don't auto-size to content. Since we use srcdoc with
   * allow-same-origin sandbox, we can access the content document to measure it.
   *
   * HOW: On load, read scrollHeight from the iframe body and set it as the iframe height.
   */
  const handleIframeLoad = useCallback(
    (e: React.SyntheticEvent<HTMLIFrameElement>) => {
      const iframe = e.currentTarget;
      try {
        const iframeDoc =
          iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc?.body) {
          // Add small buffer to prevent potential scrollbar
          const contentHeight = iframeDoc.body.scrollHeight + 16;
          iframe.style.height = `${contentHeight}px`;
        }
      } catch (err) {
        // If access is blocked, fall back to min-height from CSS
        console.warn('Could not access iframe content for auto-sizing:', err);
      }
    },
    []
  );

  if (!html) {
    return null;
  }

  return (
    <div
      className="report-table-container"
      style={{ position: 'relative', width: '100%' }}
    >
      {loading && (
        <div
          className="report-table-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
            minHeight: '200px'
          }}
        >
          <Loading description="Loading report..." withOverlay={false} />
        </div>
      )}
      <div className="report-table">
        <iframe
          srcDoc={html}
          title="Report Content"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          onLoad={handleIframeLoad}
        />
      </div>
    </div>
  );
};

export default React.memo(ReportTable);
